// src/redux/slices/customerRegistrationSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export type OptionType = { value: string; label: string };

export interface RegistrationFormValues {
  companyName: string;
  fName: string;
  mobile: string;

  telephone: string;
  fax: string;
  email: string;
  password: string;
  panNumber: string;
  gstNumber: string;
  insuranceValue: string;
  thirdPartyInsuranceValue: string;
  insuranceExpiryDate: string; // yyyy-mm-dd

  registerCategory: OptionType | null; // -> category
  natureOfBusiness: OptionType | null; // -> nature_of_business
  addressLine1: string;
  addressLine2: string;

  pinCode: string;
  state: string; // display only
  city: string; // display only
  area: OptionType | null; // value=id, label=name

  stateId?: string;
  cityId?: string;
  countryId?: string;

  uploadPanCard: File | null; // -> upload_pancard
  uploadGstCertificate: File | null; // -> upload_gst
}

type ApiOk = { status: "success" | string; message?: string };

interface CustomerRegistrationState {
  loading: boolean;
  error: string | null;
  lastResponse: any | null;
}

const initialState: CustomerRegistrationState = {
  loading: false,
  error: null,
  lastResponse: null,
};

const formatNow = () => {
  const n = new Date();
  const pad = (x: number) => (x < 10 ? `0${x}` : `${x}`);
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())} ${pad(
    n.getHours(),
  )}:${pad(n.getMinutes())}:${pad(n.getSeconds())}`;
};

export const submitCustomerRegistration = createAsyncThunk<
  ApiOk,
  RegistrationFormValues,
  { rejectValue: string }
>("customerRegistration/submit", async (v, { rejectWithValue }) => {
  try {
    const fd = new FormData();

    // Required basics
    fd.append("company_name", v.companyName);
    fd.append("full_name", v.fName);
    fd.append("category", v.registerCategory?.value ?? "");
    fd.append("nature_of_business", v.natureOfBusiness?.value ?? "");
    fd.append("reg_address1", v.addressLine1);
    fd.append("reg_address2", v.addressLine2);

    // Location
    fd.append("country_id", v.countryId ?? "");
    fd.append("state_id", v.stateId ?? "");
    fd.append("city_id", v.cityId ?? "");
    fd.append("area_id", v.area?.value ?? "");
    fd.append("pincode", v.pinCode);

    // Contacts
    fd.append("mobile_no", v.mobile);
    fd.append("tel_no", v.telephone || "");
    fd.append("fax_no", v.fax || "");
    fd.append("email", v.email);
    fd.append("password", v.password);

    // Tax/insurance
    fd.append("pan_no", v.panNumber || "");
    fd.append("gst_no", v.gstNumber || "");
    fd.append("axlpl_insurance_value", v.insuranceValue || "");
    fd.append("third_party_insurance_value", v.thirdPartyInsuranceValue || "");
    fd.append("third_party_policy_no", ""); // not in form, sending blank
    fd.append("third_party_exp_date", v.insuranceExpiryDate || ""); // yyyy-mm-dd

    // Files (only append if present)
    if (v.uploadPanCard) fd.append("upload_pancard", v.uploadPanCard);
    if (v.uploadGstCertificate) fd.append("upload_gst", v.uploadGstCertificate);

    // Optional/constant fields (from your cURL)
    fd.append("branch_id", "1");
    fd.append("poa", "");
    fd.append("tel_bill", "");
    fd.append("active", "1");
    fd.append("is_shipment_approve", "0");
    fd.append("is_send_mail", "1");
    fd.append("is_send_sms", "1");
    fd.append("last_modified", formatNow()); // e.g. 2025-09-01 17:15:27
    fd.append("last_login_ip_address", "0");
    // Not in UI: reg_certi, p_o_attorney, cust_profile_img (skip unless you add inputs)

    const res = await axios.post<ApiOk>(
      "https://my.axlpl.com/messenger/services_v7/customer_registration",
      fd,
    );

    if (res.data?.status?.toLowerCase?.() === "success") {
      return res.data;
    }
    return rejectWithValue(res.data?.message || "Registration failed");
  } catch (e: any) {
    return rejectWithValue(
      e?.response?.data?.message || e.message || "Network error",
    );
  }
});

const customerRegistrationSlice = createSlice({
  name: "customerRegistration",
  initialState,
  reducers: {
    clearRegistrationState(state) {
      state.error = null;
      state.lastResponse = null;
      state.loading = false;
    },
  },
  extraReducers: (b) => {
    b.addCase(submitCustomerRegistration.pending, (s) => {
      s.loading = true;
      s.error = null;
      s.lastResponse = null;
    });
    b.addCase(submitCustomerRegistration.fulfilled, (s, a) => {
      s.loading = false;
      s.lastResponse = a.payload;
    });
    b.addCase(submitCustomerRegistration.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload || "Registration failed";
    });
  },
});

export const { clearRegistrationState } = customerRegistrationSlice.actions;
export default customerRegistrationSlice.reducer;
